import type { Product, Order, CartItem, InventoryUpdate, ApiResponse, Metrics, PreOrder, DailyMetrics, DayMetricsResponse } from "@/types";



// --- Type Definitions (assuming they are in @/types) ---

interface PinAuthRequest {
  role: string;
  pin: string;
}

interface PinAuthResponse {
  token: string;
  role: string;
}

interface OrderCreateRequest {
  items: { item_id: string; quantity: number }[];
  payment_method: string;
  discount: number;
}

interface OrderCreateResponse extends Order {}

interface ApiError {
  error: string;
  message?: string;
  details?: any;
}

// --- Singleton API Manager ---

const TOKEN_STORAGE_KEY = "festival_pos_auth_token";
const ROLE_STORAGE_KEY = "festival_pos_auth_role";

class ApiManager {
  private static instance: ApiManager;
  private token: string | null = null;
  private role: string | null = null;
  private apiBaseUrl: string;
  private wsUrl: string;

  private constructor() {
    this.apiBaseUrl = process.env.NEXT_PUBLIC_API_BASE_URL || "";
    this.wsUrl = process.env.NEXT_PUBLIC_WS_URL || "";
    if (typeof window !== "undefined") {
      this.token = localStorage.getItem(TOKEN_STORAGE_KEY);
      this.role = localStorage.getItem(ROLE_STORAGE_KEY);
    }
  }

  public static getInstance(): ApiManager {
    if (!ApiManager.instance) {
      ApiManager.instance = new ApiManager();
    }
    return ApiManager.instance;
  }

  public getApiBaseUrl(): string {
    if (!this.apiBaseUrl) console.warn("NEXT_PUBLIC_API_BASE_URL is not set.");
    return this.apiBaseUrl;
  }

  public getWsUrl(): string {
    if (!this.wsUrl) console.warn("NEXT_PUBLIC_WS_URL is not set.");
    return this.wsUrl;
  }

  public setToken(token: string, role: string) {
    this.token = token;
    this.role = role;
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
    localStorage.setItem(ROLE_STORAGE_KEY, role);
  }

  public getToken = () => this.token;
  public getRole = () => this.role;

  public logout() {
    this.token = null;
    this.role = null;
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(ROLE_STORAGE_KEY);
    realtime.disconnect(); // Disconnect WebSocket on logout
  }

  public getHeaders(): Record<string, string> {
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (this.token) headers["Authorization"] = `Bearer ${this.token}`;
    return headers;
  }

  private async fetchWithAuth<T>(path: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    const url = `${this.getApiBaseUrl()}${path}`;
    console.log(`[API] Fetching: ${options.method || 'GET'} ${url}`);
    try {
      const response = await fetch(url, { ...options, headers: this.getHeaders() });
      if (!response.ok) {
        const contentType = response.headers.get("content-type");
        let errorData: ApiError;
        if (contentType && contentType.includes("application/json")) {
          errorData = await response.json();
        } else {
          const text = await response.text();
          errorData = { error: "API_ERROR", message: text || response.statusText };
        }
        return { success: false, ...errorData, status: response.status };
      }
      if (response.status === 204) return { success: true, data: null as T };
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      console.error("API request failed:", error);
      return { success: false, error: "NETWORK_ERROR", message: "ネットワークエラーが発生しました。" };
    }
  }

  // --- API Methods ---

  async getStationQueue(station: '1' | '2'): Promise<ApiResponse<Order[]>> {
    return this.fetchWithAuth<Order[]>(`/api/kds/stations/${station}/queue`);
  }
  async startOrder(orderId: string): Promise<ApiResponse<{ ok: boolean }>> {
    return this.fetchWithAuth<{ ok: boolean }>(`/api/kds/orders/${orderId}/start`, { method: 'POST' });
  }
  async serveOrder(orderId: string): Promise<ApiResponse<{ ok: boolean }>> {
    return this.fetchWithAuth<{ ok: boolean }>(`/api/kds/orders/${orderId}/serve`, { method: 'POST' });
  }
  async resetStation(station: '1' | '2'): Promise<ApiResponse<{ ok: boolean }>> {
    return this.fetchWithAuth<{ ok: boolean }>(`/api/kds/stations/${station}/reset`, { method: 'POST' });
  }

  async undoKdsOrder(orderId: string): Promise<ApiResponse<{ ok: boolean }>> {
    return this.fetchWithAuth<{ ok: boolean }>(`/api/kds/orders/${orderId}/undo`, { method: 'POST' });
  }

  // Handover-specific endpoints
  async handoverComplete(orderId: string): Promise<ApiResponse<{ ok: boolean; order?: any }>> {
    return this.fetchWithAuth<{ ok: boolean; order?: any }>(`/api/handover/orders/${orderId}/complete`, { method: 'POST' });
  }
  async handoverConfirmCancel(orderId: string): Promise<ApiResponse<{ ok: boolean }>> {
    return this.fetchWithAuth<{ ok: boolean }>(`/api/handover/orders/${orderId}/confirm-cancel`, { method: 'POST' });
  }
  async handoverUndoConfirmCancel(orderId: string): Promise<ApiResponse<{ ok: boolean }>> {
    return this.fetchWithAuth<{ ok: boolean }>(`/api/handover/orders/${orderId}/undo-confirm-cancel`, { method: 'POST' });
  }

  async login(req: PinAuthRequest): Promise<ApiResponse<PinAuthResponse>> {
    const res = await this.fetchWithAuth<PinAuthResponse>("/api/auth/pin", { method: "POST", body: JSON.stringify(req) });
    if (res.success && res.data) {
      this.setToken(res.data.token, res.data.role);
      realtime.connect(); // Connect WebSocket on login
    }
    return res;
  }

  async verifyPin(pin: string): Promise<ApiResponse<{ success: boolean }>> {
    return this.fetchWithAuth<{ success: boolean }>("/api/pin/verify", { method: "POST", body: JSON.stringify({ pin }) });
  }

  async getInventory(options: { include_inactive?: boolean } = {}): Promise<ApiResponse<Product[]>> {
    const params = new URLSearchParams();
    if (options.include_inactive) {
      params.set("include_inactive", "true");
    }
    const queryString = params.toString();
    return this.fetchWithAuth<Product[]>(`/api/products${queryString ? `?${queryString}` : ''}`);
  }

  async patchInventory(updates: InventoryUpdate[]): Promise<ApiResponse<{ ok: boolean }>> {
    return this.fetchWithAuth<{ ok: boolean }>("/api/inventory", { method: "PATCH", body: JSON.stringify({ updates }) });
  }

  async adjustInventory(adjustments: { productId: string, change: number }[]): Promise<ApiResponse<{ ok: boolean }>> {
    return this.fetchWithAuth<{ ok: boolean }>("/api/inventory/adjust", { method: "POST", body: JSON.stringify({ adjustments }) });
  }

  async postOrder(orderData: OrderCreateRequest): Promise<ApiResponse<OrderCreateResponse>> {
    return this.fetchWithAuth<OrderCreateResponse>("/api/orders", { method: "POST", body: JSON.stringify(orderData) });
  }

  async getOrders(filter?: { status?: string }): Promise<ApiResponse<Order[]>> {
    const params = new URLSearchParams();
    if (filter?.status) params.append("status", filter.status);
    return this.fetchWithAuth<Order[]>(`/api/orders?${params.toString()}`);
  }

  async updateOrderStatus(orderId: string, status: Order["status"]): Promise<ApiResponse<Order>> {
    if (status === 'canceled') {
      return this.undoOrder(orderId);
    }
    return this.fetchWithAuth<Order>(`/api/orders/${orderId}/status`, {
      method: "POST",
      body: JSON.stringify({ status }),
    });
  }

  async undoOrder(orderId: string): Promise<ApiResponse<any>> {
    return this.fetchWithAuth<any>(`/api/orders/${orderId}/undo`, { method: "POST" });
  }

  

  

  async getMetrics(): Promise<ApiResponse<Metrics>> {
    return this.fetchWithAuth<Metrics>("/api/metrics");
  }

  async getDailyMetrics(params?: { from?: string; to?: string }): Promise<ApiResponse<DailyMetrics>> {
    const sp = new URLSearchParams();
    if (params?.from) sp.set("from", params.from);
    if (params?.to) sp.set("to", params.to);
    return this.fetchWithAuth<DailyMetrics>(`/api/metrics/daily?${sp.toString()}`);
  }

  // 追加: 日ごとメトリクス（売上がある日だけ、降順）
  async getMetricsByDay(params?: { limit?: number }): Promise<ApiResponse<DayMetricsResponse>> {
    const sp = new URLSearchParams();
    if (params?.limit) sp.set("limit", String(params.limit));
    return this.fetchWithAuth<DayMetricsResponse>(`/api/metrics/by-day?${sp.toString()}`);
  }

  async getPreorderMode(): Promise<ApiResponse<{ enabled: boolean }>> {
    return this.fetchWithAuth<{ enabled: boolean }>("/api/settings/preorder-mode");
  }

  async setPreorderMode(enabled: boolean): Promise<ApiResponse<{ success: boolean }>> {
    return this.fetchWithAuth<{ success: boolean }>("/api/settings/preorder-mode", { method: "POST", body: JSON.stringify({ enabled }) });
  }

  // Assignment Mode
  async getAssignmentMode(): Promise<ApiResponse<{ mode: 'balanced' | 'all-S1' }>> {
    return this.fetchWithAuth<{ mode: 'balanced' | 'all-S1' }>("/api/settings/assignment-mode");
  }

  async setAssignmentMode(mode: 'balanced' | 'all-S1'): Promise<ApiResponse<{ success: true; mode: 'balanced' | 'all-S1' }>> {
    return this.fetchWithAuth<{ success: true; mode: 'balanced' | 'all-S1' }>("/api/settings/assignment-mode", { method: "POST", body: JSON.stringify({ mode }) });
  }

  // Admin: release reserved stock of unpaid preorders (PIN required, double guard)
  async adminReleasePreorders(pin: string): Promise<ApiResponse<{ ok: boolean; released: number; products: { id: string; stock: number }[] }>> {
    return this.fetchWithAuth<{ ok: boolean; released: number; products: { id: string; stock: number }[] }>(
      "/api/admin/preorders/release",
      { method: "POST", body: JSON.stringify({ pin }) }
    );
  }

  // --- Master Admin Methods ---
  async getMasterOrders(params?: { cursor?: string | null; no?: string }): Promise<ApiResponse<{ data: Order[], nextCursor: string | null }>> {
    const searchParams = new URLSearchParams();
    if (params?.cursor) searchParams.set('cursor', params.cursor);
    if (params?.no) {
      const normalized = params.no.trim().replace(/^#/, "");
      searchParams.set('no', normalized);
    }
    return this.fetchWithAuth<{ data: Order[], nextCursor: string | null }>(`/api/master/orders?${searchParams.toString()}`);
  }

  async getMasterPreorders(params?: { cursor?: string | null; no?: string }): Promise<ApiResponse<{ data: PreOrder[], nextCursor: string | null }>> {
    const searchParams = new URLSearchParams();
    if (params?.cursor) searchParams.set('cursor', params.cursor);
    if (params?.no) {
      const normalized = params.no.trim().replace(/^#/, "");
      searchParams.set('no', normalized);
    }
    return this.fetchWithAuth<{ data: PreOrder[], nextCursor: string | null }>(`/api/master/preorders?${searchParams.toString()}`);
  }

  async deleteMasterOrder(orderId: string): Promise<ApiResponse<{ ok: boolean }>> {
    return this.fetchWithAuth<{ ok: boolean }>(`/api/master/orders/${orderId}`, { method: "DELETE" });
  }

  async updateMasterOrderStatus(orderId: string, status: Order['status']): Promise<ApiResponse<Order>> {
    return this.fetchWithAuth<Order>(`/api/master/orders/${orderId}`, { 
      method: "PATCH", 
      body: JSON.stringify({ status })
    });
  }

  async deleteMasterPreorder(preorderId: string): Promise<ApiResponse<{ ok: boolean }>> {
    return this.fetchWithAuth<{ ok: boolean }>(`/api/master/preorders/${preorderId}`, { method: "DELETE" });
  }

  async updateMasterPreorderStatus(preorderId: string, status: PreOrder['status']): Promise<ApiResponse<PreOrder>> {
    return this.fetchWithAuth<PreOrder>(`/api/master/preorders/${preorderId}`, { 
      method: "PATCH", 
      body: JSON.stringify({ status })
    });
  }

  async exportPreordersCsv(
  mode: "summary" | "lines" | "wide" = "summary"
): Promise<{ success: true; blob: Blob; filename: string } | { success: false; message?: string; error?: string }> {
  try {
    // 1) すべての前売りをページングで収集
    let cursor: string | null = null;
    const all: PreOrder[] = [];

    do {
      const res = await this.getMasterPreorders({ cursor });
      if (!res.success || !res.data) {
        return { success: false, message: res.message || "Failed to fetch preorders" };
      }
      all.push(...res.data.data);
      cursor = res.data.nextCursor;
    } while (cursor);

    // 2) CSVビルダー（BOM, CRLF, 厳密エスケープ）
    const esc = (v: any) => {
      const s = String(v ?? "");
      // 改行はセル内改行として許容。CSVルール上はダブルクォートで囲めばOK。
      const needsQuote = /[\",\n\r]/.test(s);
      if (needsQuote) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };

    const EOL = "\r\n"; // Excel/Sheetsで安定
    let header: string[];
    const rows: string[] = [];

    if (mode === "summary") {
      // 1行=1注文（itemsは「商品名x数量 | …」の文字列にまとめる）
      header = ["preorder_no", "status", "total", "created_at", "items"];
      for (const p of all) {
        const created = new Date(p.createdAt).toISOString();
        const items = p.items
          .map((i) => {
            const name = (i as any).productName ?? (i as any).name ?? "";
            const qty = (i as any).quantity ?? 0;
            return `${String(name).replace(/\r?\n/g, " ")}x${qty}`;
          })
          .join(" | ");
        const line = [
          esc(p.preorderNo),
          esc(p.status),
          esc(p.total),
          esc(created),
          esc(items),
        ].join(",");
        rows.push(line);
      }
    } else if (mode === "lines") {
      // mode === "lines": 1行=1明細（ピボットしやすい出力）
      header = [
        "preorder_no",
        "status",
        "order_total",
        "created_at",
        "product_id",
        "product_name",
        "quantity",
        "unit_price",
        "line_total",
      ];
      for (const p of all) {
        const created = new Date(p.createdAt).toISOString();
        for (const it of p.items) {
          const productId = (it as any).productId ?? (it as any).item_id ?? "";
          const productName = (it as any).productName ?? (it as any).name ?? "";
          const quantity = (it as any).quantity ?? 0;
          const unitPrice = (it as any).unitPrice ?? (it as any).price ?? 0;
          const line = [
            esc(p.preorderNo),
            esc(p.status),
            esc(p.total),
            esc(created),
            esc(productId),
            esc(String(productName).replace(/\r?\n/g, " ")),
            esc(quantity),
            esc(unitPrice),
            esc(unitPrice * quantity),
          ].join(",");
          rows.push(line);
        }
        // 明細が0の場合も行を一つ出すなら、ここで追加してもよい
      }
    } else {
      // mode === "wide": 商品ごとに列を展開して数量を配置（無ければ0）
      // 1) 全商品の一覧を作成（登場順）
      const productOrder: string[] = [];
      const productNameMap: Record<string, string> = {};
      for (const p of all) {
        for (const it of p.items) {
          const pid = (it as any).productId ?? (it as any).item_id ?? "";
          const pname = String((it as any).productName ?? (it as any).name ?? "").replace(/\r?\n/g, " ");
          if (pid && !productOrder.includes(pid)) {
            productOrder.push(pid);
            productNameMap[pid] = pname || pid;
          }
        }
      }
      header = ["preorder_no", "status", "total", "created_at", ...productOrder.map(pid => productNameMap[pid])];
      for (const p of all) {
        const created = new Date(p.createdAt).toISOString();
        const qtyMap: Record<string, number> = {};
        for (const it of p.items) {
          const pid = (it as any).productId ?? (it as any).item_id ?? "";
          const q = (it as any).quantity ?? 0;
          if (!pid) continue;
          qtyMap[pid] = (qtyMap[pid] || 0) + q;
        }
        const cols = productOrder.map(pid => esc(qtyMap[pid] ?? 0));
        const line = [
          esc(p.preorderNo),
          esc(p.status),
          esc(p.total),
          esc(created),
          ...cols,
        ].join(",");
        rows.push(line);
      }
    }

    const csvCore = [header.join(","), ...rows].join(EOL) + EOL;

    // 3) UTF-8 BOM 付きで作成（Excel対策）
    const BOM = "\uFEFF";
    const blob = new Blob([BOM + csvCore], { type: "text/csv;charset=utf-8" });

    const ts = new Date();
    const pad = (n: number) => String(n).padStart(2, "0");
    const base = mode === "lines" ? "preorders_lines" : (mode === "wide" ? "preorders_wide" : "preorders_summary");
    const filename = `${base}_${ts.getFullYear()}${pad(ts.getMonth() + 1)}${pad(ts.getDate())}_${pad(ts.getHours())}${pad(
      ts.getMinutes()
    )}${pad(ts.getSeconds())}.csv`;

    return { success: true, blob, filename };
  } catch (e: any) {
    return { success: false, message: e?.message || "Failed to export CSV" };
  }
}

  async exportOrdersCsv(
    mode: "lines" | "wide" = "wide"
  ): Promise<{ success: true; blob: Blob; filename: string } | { success: false; message?: string; error?: string }> {
    try {
      // 1) すべての一般注文をページングで収集
      let cursor: string | null = null;
      const all: Order[] = [];

      do {
        const res = await this.getMasterOrders({ cursor });
        if (!res.success || !res.data) {
          return { success: false, message: res.message || "Failed to fetch orders" };
        }
        all.push(...res.data.data);
        cursor = res.data.nextCursor;
      } while (cursor);

      // 2) CSVビルダー
      const esc = (v: any) => {
        const s = String(v ?? "");
        const needsQuote = /[\",\n\r]/.test(s);
        return needsQuote ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const EOL = "\r\n";
      let header: string[];
      const rows: string[] = [];

      if (mode === "lines") {
        header = [
          "order_no",
          "status",
          "total",
          "created_at",
          "station",
          "product_id",
          "product_name",
          "quantity",
          "unit_price",
          "line_total",
        ];
        for (const o of all) {
          const created = new Date(o.createdAt).toISOString();
          const station = (o as any).assignedStation ?? (o as any).station ?? "";
          for (const it of o.items) {
            const productId = (it as any).productId ?? (it as any).item_id ?? "";
            const productName = (it as any).productName ?? (it as any).name ?? "";
            const quantity = (it as any).quantity ?? 0;
            const unitPrice = (it as any).unitPrice ?? (it as any).price ?? 0;
            const line = [
              esc(o.orderNo),
              esc(o.status),
              esc(o.total),
              esc(created),
              esc(station),
              esc(productId),
              esc(String(productName).replace(/\r?\n/g, " ")),
              esc(quantity),
              esc(unitPrice),
              esc(unitPrice * quantity),
            ].join(",");
            rows.push(line);
          }
        }
      } else {
        // mode === "wide": 商品ごとに列を展開
        const productOrder: string[] = [];
        const productNameMap: Record<string, string> = {};
        for (const o of all) {
          for (const it of o.items) {
            const pid = (it as any).productId ?? (it as any).item_id ?? "";
            const pname = String((it as any).productName ?? (it as any).name ?? "").replace(/\r?\n/g, " ");
            if (pid && !productOrder.includes(pid)) {
              productOrder.push(pid);
              productNameMap[pid] = pname || pid;
            }
          }
        }
        header = ["order_no", "status", "total", "created_at", "station", ...productOrder.map(pid => productNameMap[pid])];
        for (const o of all) {
          const created = new Date(o.createdAt).toISOString();
          const station = (o as any).assignedStation ?? (o as any).station ?? "";
          const qtyMap: Record<string, number> = {};
          for (const it of o.items) {
            const pid = (it as any).productId ?? (it as any).item_id ?? "";
            const q = (it as any).quantity ?? 0;
            if (!pid) continue;
            qtyMap[pid] = (qtyMap[pid] || 0) + q;
          }
          const cols = productOrder.map(pid => esc(qtyMap[pid] ?? 0));
          const line = [
            esc(o.orderNo),
            esc(o.status),
            esc(o.total),
            esc(created),
            esc(station),
            ...cols,
          ].join(",");
          rows.push(line);
        }
      }

      const csvCore = [header.join(","), ...rows].join(EOL) + EOL;
      const BOM = "\uFEFF";
      const blob = new Blob([BOM + csvCore], { type: "text/csv;charset=utf-8" });
      const ts = new Date();
      const pad = (n: number) => String(n).padStart(2, "0");
      const base = mode === "lines" ? "orders_lines" : "orders_wide";
      const filename = `${base}_${ts.getFullYear()}${pad(ts.getMonth() + 1)}${pad(ts.getDate())}_${pad(ts.getHours())}${pad(ts.getMinutes())}${pad(ts.getSeconds())}.csv`;
      return { success: true, blob, filename };
    } catch (e: any) {
      return { success: false, message: e?.message || "Failed to export CSV" };
    }
  }

  // --- Pre-order Methods ---

  async postPreOrder(orderData: OrderCreateRequest): Promise<ApiResponse<PreOrder>> {
    return this.fetchWithAuth<PreOrder>("/api/preorders", { method: "POST", body: JSON.stringify(orderData) });
  }

  async getPreOrders(filter?: { no?: string; redeemed?: boolean; limit?: number; cursor?: string }): Promise<ApiResponse<{ data: PreOrder[]; nextCursor: string | null }>> {
    const params = new URLSearchParams();
    if (filter?.no) {
      const no = filter.no.trim().replace(/^#/, "");
      params.set("no", no);
    } else {
      // 明示されていなければ未引換のみをデフォルト
      if (typeof filter?.redeemed === "boolean") {
        params.set("redeemed", String(filter.redeemed));
      } else {
        params.set("redeemed", "false");
      }
    }

    if (filter?.limit) {
      params.set("limit", String(filter.limit));
    }
    if (filter?.cursor) {
      params.set("cursor", filter.cursor);
    }
    return this.fetchWithAuth<{ data: PreOrder[]; nextCursor: string | null }>(`/api/preorders?${params.toString()}`);
  }

  async redeemPreOrder(preorderId: string): Promise<ApiResponse<{ ok: boolean; orderId: string }>> {
    return this.fetchWithAuth<{ ok: boolean; orderId: string }>(`/api/preorders/${preorderId}/redeem`, { method: "POST" });
  }
  async receptionUndoRedeem(preorderId: string): Promise<ApiResponse<{ ok: boolean; preorder?: any }>> {
    return this.fetchWithAuth<{ ok: boolean; preorder?: any }>(`/api/reception/preorders/${preorderId}/undo-redeem`, { method: 'POST' });
  }

  async receptionRefundPreorder(preorderId: string): Promise<ApiResponse<{ ok: boolean; preorder?: any }>> {
    return this.fetchWithAuth<{ ok: boolean; preorder?: any }>(`/api/reception/preorders/${preorderId}/refund`, { method: 'POST' });
  }

  async receptionUndoRefund(preorderId: string): Promise<ApiResponse<{ ok: boolean; preorder?: any }>> {
    return this.fetchWithAuth<{ ok: boolean; preorder?: any }>(`/api/reception/preorders/${preorderId}/undo-refund`, { method: 'POST' });
  }
}

// --- WebSocket Connection ---

type EventCallback<T> = (data: T) => void;
type ConnectionStatus = "connected" | "disconnected" | "error";

export interface RealtimeConnection {
  connect(): void;
  disconnect(): void;
  onOrderUpdate(cb: EventCallback<any>): () => void;
  onPreOrderUpdate(cb: EventCallback<any>): () => void;
  onInventoryUpdate(cb: EventCallback<any>): () => void;
  onPreorderModeUpdate(cb: EventCallback<any>): () => void;
  onConnectionChange(cb: EventCallback<ConnectionStatus>): () => void;
}

class WebSocketConnection implements RealtimeConnection {
  private ws: WebSocket | null = null;
  private reconnectTimer: any = null;
  private backoffMs = 1000;
  private readonly maxBackoffMs = 15000;

  private listeners = {
    orderUpdate: new Set<EventCallback<any>>(),
    preOrderUpdate: new Set<EventCallback<any>>(),
    inventoryUpdate: new Set<EventCallback<any>>(),
    preorderModeUpdate: new Set<EventCallback<any>>(),
    connectionChange: new Set<EventCallback<ConnectionStatus>>(),
  };

  private notify<T>(listenerSet: Set<EventCallback<T>>, data: T) {
    listenerSet.forEach(cb => cb(data));
  }

  connect() {
    const token = ApiManager.getInstance().getToken();
    const wsUrl = ApiManager.getInstance().getWsUrl();
    if (!token || !wsUrl) {
      console.error("WebSocket: token or URL missing");
      this.notify(this.listeners.connectionChange, "error");
      return;
    }
    if (this.ws) this.disconnect();

    const url = `${wsUrl.replace(/\/ws$/, "")}/ws?token=${encodeURIComponent(token)}`;
    this.ws = new WebSocket(url);

    this.ws.onopen = () => {
      this.notify(this.listeners.connectionChange, "connected");
      this.backoffMs = 1000;
    };
    this.ws.onclose = () => {
      this.notify(this.listeners.connectionChange, "disconnected");
      this.scheduleReconnect();
    };
    this.ws.onerror = () => {
      this.notify(this.listeners.connectionChange, "error");
      this.ws?.close();
    };

      this.ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          switch (message.event) {
            case 'hello': break;
            case 'ping': this.ws?.send(JSON.stringify({ event: 'pong', ts: Date.now() })); break;
            case 'order.created':
            case 'order.updated':
              this.notify(this.listeners.orderUpdate, message);
              break;
            case 'preorder.created':
            case 'preorder.redeemed':
            case 'preorder.updated':
            case 'preorder.deleted':
            case 'preorder.release.executed':
              this.notify(this.listeners.preOrderUpdate, message);
              break;
            case 'inventory.updated':
              this.notify(this.listeners.inventoryUpdate, message);
              break;
            case 'setting.preorder.updated':
              this.notify(this.listeners.preorderModeUpdate, message);
              break;
          }
        } catch (e) { console.error("WS parse error:", e); }
      };
  }

  private scheduleReconnect() {
    if (this.reconnectTimer || !ApiManager.getInstance().getToken()) return; // Do not reconnect if logged out
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.backoffMs = Math.min(this.backoffMs * 2, this.maxBackoffMs);
      this.connect();
    }, this.backoffMs);
  }

  disconnect() {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    if (this.ws) {
      this.ws.onmessage = this.ws.onclose = this.ws.onerror = null;
      this.ws.close();
      this.ws = null;
    }
    this.notify(this.listeners.connectionChange, "disconnected");
  }
  
  private subscribe<T>(listenerSet: Set<EventCallback<T>>, cb: EventCallback<T>): () => void {
    listenerSet.add(cb);
    return () => listenerSet.delete(cb);
  }

  onOrderUpdate = (cb: EventCallback<any>) => this.subscribe(this.listeners.orderUpdate, cb);
  onPreOrderUpdate = (cb: EventCallback<any>) => this.subscribe(this.listeners.preOrderUpdate, cb);
  onInventoryUpdate = (cb: EventCallback<any>) => this.subscribe(this.listeners.inventoryUpdate, cb);
  onPreorderModeUpdate = (cb: EventCallback<any>) => this.subscribe(this.listeners.preorderModeUpdate, cb);
  onConnectionChange = (cb: EventCallback<ConnectionStatus>) => this.subscribe(this.listeners.connectionChange, cb);
}

// --- Export Instances ---

export const api = ApiManager.getInstance();
export const realtime = new WebSocketConnection();
